import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { GPTModelItem } from "@/api/models"
import type { ReasoningMode } from "@/stores/ai-explorer-store"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { EffortSlider } from "@/features/chat/components/effort-slider"
import { ReasoningModePill } from "@/features/chat/components/reasoning-mode-pill"
import { groupLegacyModels, isCurrentModel } from "@/features/chat/components/reasoning-model-utils"
import {
  DEFAULT_REASONING_PRESETS,
  normalizeReasoningPresets,
  type ReasoningPreset,
  type ReasoningPresets,
} from "@/features/chat/utils/reasoning-presets"
import { useAvailableGPTModels } from "@/hooks/use-config-query"

import { EditExplorerReasoningPresets } from "./edit-explorer-reasoning-presets"

export interface ExplorerFastRouterConfig {
  deterministic_pregate_enabled: boolean
  model_router_enabled: boolean
  confidence_threshold: number
  timeout_ms: number
  fast_model_id: string
  reasoning_effort: "none" | "low" | "medium"
  model_routing_enabled: boolean
  serving_model_allowlist: string[]
  reasoning_presets: ReasoningPresets
}

interface EditExplorerFastRouterDialogProps {
  isOpen: boolean
  config: ExplorerFastRouterConfig
  onSave: (config: ExplorerFastRouterConfig) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function EditExplorerFastRouterDialog({
  isOpen,
  config,
  onSave,
  onOpenChange,
}: EditExplorerFastRouterDialogProps) {
  const { t } = useTranslation()
  const { models, isLoading: modelsLoading } = useAvailableGPTModels("explorer")
  const [saving, setSaving] = useState(false)
  const [deterministicPregateEnabled, setDeterministicPregateEnabled] = useState(true)
  const [modelRouterEnabled, setModelRouterEnabled] = useState(true)
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.85)
  const [timeoutMs, setTimeoutMs] = useState(1500)
  const [fastModelId, setFastModelId] = useState("")
  const [reasoningEffort, setReasoningEffort] =
    useState<ExplorerFastRouterConfig["reasoning_effort"]>("low")
  const [modelRoutingEnabled, setModelRoutingEnabled] = useState(false)
  const [servingModelAllowlist, setServingModelAllowlist] = useState<string[]>([])
  const [reasoningPresets, setReasoningPresets] =
    useState<ReasoningPresets>(DEFAULT_REASONING_PRESETS)

  const selectedModel = useMemo(
    () => models.find((model) => model.id === fastModelId),
    [fastModelId, models]
  )

  const presetModels = useMemo(
    () =>
      models
        .filter((model) => model.id !== "auto")
        .map((model) => ({ id: model.id, displayName: model.display_name })),
    [models]
  )

  useEffect(() => {
    setDeterministicPregateEnabled(config.deterministic_pregate_enabled)
    setModelRouterEnabled(config.model_router_enabled)
    setConfidenceThreshold(config.confidence_threshold)
    setTimeoutMs(config.timeout_ms)
    setFastModelId(config.fast_model_id || "")
    setReasoningEffort(config.reasoning_effort)
    // Backend GET normalizes the config, so these keys are always present.
    setModelRoutingEnabled(config.model_routing_enabled)
    setServingModelAllowlist(config.serving_model_allowlist)
    setReasoningPresets(normalizeReasoningPresets(config.reasoning_presets))
  }, [config])

  const toggleServingModel = (modelId: string, checked: boolean) => {
    setServingModelAllowlist((current) =>
      checked
        ? [...current.filter((id) => id !== modelId), modelId]
        : current.filter((id) => id !== modelId)
    )
  }

  // Allowlist grouping mirrors the chat model selector: current models first,
  // then legacy models bucketed into the same family sections.
  const allowlistModels = models.filter((model) => model.id !== "auto")
  const currentAllowlistModels = allowlistModels.filter(isCurrentModel)
  const legacyAllowlistModels = allowlistModels.filter((model) => !isCurrentModel(model))
  const legacyAllowlistGroups = groupLegacyModels(legacyAllowlistModels)

  const updatePreset = (key: ReasoningMode, patch: Partial<ReasoningPreset>) => {
    setReasoningPresets((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }))
  }

  const renderAllowlistRow = (model: GPTModelItem) => (
    <label
      key={model.id}
      className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded p-1.5"
      htmlFor={`explorer-serving-model-${model.id}`}
    >
      <Checkbox
        checked={servingModelAllowlist.includes(model.id)}
        id={`explorer-serving-model-${model.id}`}
        onCheckedChange={(checked) => toggleServingModel(model.id, checked === true)}
      />
      <span className="flex flex-col">
        <span className="text-sm">{model.display_name}</span>
        <span className="text-muted-foreground text-xs">{model.id}</span>
      </span>
    </label>
  )

  const handleSubmit = async () => {
    if (confidenceThreshold < 0 || confidenceThreshold > 1) {
      toast.error(
        t("adminDashboard.features.explorerFastRouter.confidenceError", {
          defaultValue: "Confidence threshold must be between 0 and 1",
        })
      )
      return
    }
    if (timeoutMs < 250 || timeoutMs > 10000) {
      toast.error(
        t("adminDashboard.features.explorerFastRouter.timeoutError", {
          defaultValue: "Timeout must be between 250 and 10000 ms",
        })
      )
      return
    }
    if (modelRoutingEnabled && servingModelAllowlist.length < 2) {
      toast.error(
        t("adminDashboard.features.explorerFastRouter.allowlistError", {
          defaultValue: "Auto model selection needs at least 2 models in the allowlist",
        })
      )
      return
    }

    for (const key of ["fast", "thinking", "big_brain"] as const) {
      const preset = reasoningPresets[key]
      if (!preset.model_id.trim()) {
        toast.error(
          t("adminDashboard.features.explorerFastRouter.presetModelRequired", {
            defaultValue: "Each reasoning preset needs a model",
          })
        )
        return
      }
      if (!preset.effort) {
        toast.error(
          t("adminDashboard.features.explorerFastRouter.presetEffortRequired", {
            defaultValue: "Each reasoning preset needs an effort level",
          })
        )
        return
      }
    }

    setSaving(true)
    try {
      await onSave({
        deterministic_pregate_enabled: deterministicPregateEnabled,
        model_router_enabled: modelRouterEnabled,
        confidence_threshold: confidenceThreshold,
        timeout_ms: timeoutMs,
        fast_model_id: fastModelId,
        reasoning_effort: reasoningEffort,
        model_routing_enabled: modelRoutingEnabled,
        serving_model_allowlist: servingModelAllowlist,
        reasoning_presets: {
          auto: reasoningPresets.auto,
          fast: reasoningPresets.fast,
          thinking: reasoningPresets.thinking,
          big_brain: reasoningPresets.big_brain,
        },
      })
      toast.success(
        t("adminDashboard.features.explorerFastRouter.success", {
          defaultValue: "Fast router configuration updated",
        })
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.explorerFastRouter.error", {
          defaultValue: "Failed to update fast router configuration",
        }) + (error instanceof Error ? `: ${error.message}` : "")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.features.explorerFastRouter.configTitle", {
              defaultValue: "Explorer Fast Router",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.features.explorerFastRouter.configDescription", {
              defaultValue:
                "Configure the deterministic pre-gate, low-cost model classifier, Auto model selection, and Explorer reasoning presets (/fast, /thinking, /bigbrain).",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-4">
            <div className="space-y-0.5">
              <Label>
                {t("adminDashboard.features.explorerFastRouter.routerSectionLabel", {
                  defaultValue: "Router",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.routerSectionHelp", {
                  defaultValue: "One fast model call per turn powers both features below.",
                })}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="explorer-fast-router-model">
                {t("adminDashboard.features.explorerFastRouter.modelLabel", {
                  defaultValue: "Router model",
                })}
              </Label>
              <div id="explorer-fast-router-model">
                <ReasoningModePill
                  availableModels={models}
                  disabled={modelsLoading}
                  modelOverride={fastModelId || null}
                  selectedModel={selectedModel}
                  showEffortSection={false}
                  onModelOverrideChange={(id) =>
                    setFastModelId(id === "auto" || id == null ? "" : id)
                  }
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {fastModelId
                  ? t("adminDashboard.features.explorerFastRouter.selectedModelHelp", {
                      defaultValue: "Selected model ID: {{id}}",
                      id: fastModelId,
                    })
                  : t("adminDashboard.features.explorerFastRouter.modelHelp", {
                      defaultValue:
                        "Used for both direct-answer decisions and Auto model selection. Leave on Auto unless you want a specific fast model.",
                    })}
              </p>
            </div>

            <div className="space-y-2">
              {/* EffortSlider renders its own "Reasoning effort" caption — no outer Label. */}
              <div id="explorer-fast-router-reasoning">
                <EffortSlider
                  efforts={["none", "low", "medium"]}
                  value={reasoningEffort}
                  onChange={(effort) =>
                    setReasoningEffort(effort as ExplorerFastRouterConfig["reasoning_effort"])
                  }
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.routerEffortHelp", {
                  defaultValue:
                    "Effort of the router's own classification call only. It does not set the effort of the answer; for Auto, see the minimum effort under Reasoning presets.",
                })}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="explorer-fast-router-confidence">
                  {t("adminDashboard.features.explorerFastRouter.confidenceLabel", {
                    defaultValue: "Confidence threshold",
                  })}
                </Label>
                <Input
                  id="explorer-fast-router-confidence"
                  max="1"
                  min="0"
                  step="0.01"
                  type="number"
                  value={confidenceThreshold}
                  onChange={(event) => {
                    const parsed = Number(event.target.value)
                    setConfidenceThreshold(Number.isNaN(parsed) ? 0.85 : parsed)
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="explorer-fast-router-timeout">
                  {t("adminDashboard.features.explorerFastRouter.timeoutLabel", {
                    defaultValue: "Router timeout (ms)",
                  })}
                </Label>
                <Input
                  id="explorer-fast-router-timeout"
                  max="10000"
                  min="250"
                  step="100"
                  type="number"
                  value={timeoutMs}
                  onChange={(event) => {
                    const parsed = Number(event.target.value)
                    setTimeoutMs(Number.isNaN(parsed) ? 1500 : Math.trunc(parsed))
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="explorer-fast-router-model-router">
                {t("adminDashboard.features.explorerFastRouter.modelRouterLabel", {
                  defaultValue: "Direct answers for trivial prompts",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.modelRouterHelp", {
                  defaultValue:
                    "A cheap model may answer obviously trivial prompts (greetings, clock questions) immediately, without loading the full tool pipeline. Uncertain prompts always use full Explorer.",
                })}
              </p>
            </div>
            <Switch
              checked={modelRouterEnabled}
              id="explorer-fast-router-model-router"
              onCheckedChange={setModelRouterEnabled}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="explorer-fast-router-pregate">
                {t("adminDashboard.features.explorerFastRouter.pregateLabel", {
                  defaultValue: "Use deterministic pre-gate",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.pregateHelp", {
                  defaultValue:
                    "Only obvious simple prompts reach the model classifier. Complex/contextual prompts go straight to full Explorer.",
                })}
              </p>
            </div>
            <Switch
              checked={deterministicPregateEnabled}
              id="explorer-fast-router-pregate"
              onCheckedChange={setDeterministicPregateEnabled}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="explorer-fast-router-model-routing">
                {t("adminDashboard.features.explorerFastRouter.modelRoutingLabel", {
                  defaultValue: "Auto model selection",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.modelRoutingHelp", {
                  defaultValue:
                    "When the user picks Auto, the router chooses the serving model for the session from the allowlist below. Sticky per session; manual selection overrides. Both features share a single router call per turn.",
                })}
              </p>
            </div>
            <Switch
              checked={modelRoutingEnabled}
              id="explorer-fast-router-model-routing"
              onCheckedChange={setModelRoutingEnabled}
            />
          </div>

          {modelRoutingEnabled && (
            <div className="space-y-2">
              <Label>
                {t("adminDashboard.features.explorerFastRouter.allowlistLabel", {
                  defaultValue: "Serving model allowlist",
                })}
              </Label>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {modelsLoading ? (
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.features.explorerFastRouter.modelsLoading", {
                      defaultValue: "Loading models…",
                    })}
                  </p>
                ) : (
                  <>
                    {currentAllowlistModels.map(renderAllowlistRow)}
                    {legacyAllowlistGroups.length > 0 && (
                      <>
                        <div className="my-1 border-t" />
                        <p className="text-muted-foreground px-1.5 pt-1 pb-0.5 text-xs font-medium">
                          {t("reasoning_mode.legacy", { defaultValue: "Legacy" })}
                        </p>
                        {legacyAllowlistGroups.map((group) => (
                          <div key={group.key}>
                            <p className="text-muted-foreground px-1.5 pt-1 pb-0.5 text-xs font-normal">
                              {t(group.labelKey, { defaultValue: group.labelDefault })}
                            </p>
                            {group.models.map(renderAllowlistRow)}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.allowlistHelp", {
                  defaultValue:
                    "The router only ever picks from these models (minimum 2). Users still need access to a model for it to be considered.",
                })}
              </p>
            </div>
          )}

          <EditExplorerReasoningPresets
            modelRoutingEnabled={modelRoutingEnabled}
            models={presetModels}
            modelsLoading={modelsLoading}
            reasoningPresets={reasoningPresets}
            onUpdatePreset={updatePreset}
          />
        </div>

        <DialogFooter>
          <Button disabled={saving} variant="outline" onClick={() => onOpenChange(false)}>
            {t("adminDashboard.features.explorerFastRouter.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={saving} onClick={handleSubmit}>
            {saving
              ? t("adminDashboard.features.explorerFastRouter.saving", {
                  defaultValue: "Saving...",
                })
              : t("adminDashboard.features.explorerFastRouter.save", {
                  defaultValue: "Save",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
